import React, { useState, useRef, useEffect } from "react";
import Header from "../components/Header";
import Banner from "../components/Banner";
import { toast } from "react-toastify";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import StyledButton from "../components/elements/StyledButton";
import AddDashboardDialog from "../components/AddDashboardDialog";
import {
  embedDashboard,
} from "@superset-ui/embedded-sdk";
import { getCredentials, getDashboards, handleEmbedDashboard, saveDashboards } from "../services/api/dashboard.api";
import { Link, useNavigate } from "react-router-dom";
import { getCurrentUserDetails } from "../services/api/users.api";
const SUPERSET_BASE_URL = 'https://napadashboard.edotcogroup.com';

// Custom hook for handling access token
const useAccessToken = () => {
  const [accessToken, setAccessToken] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem("superset_access_token");
    if (storedToken) {
      setAccessToken(storedToken);
    } else {
      fetchCredentials();
    }
  }, []);

  const fetchCredentials = async () => {
    try {
      const response = await getCredentials();
      setAccessToken(response.access_token);
      localStorage.setItem("superset_access_token", response.access_token);
    } catch (error) {
      console.error("Error signing in:", error);
    }
  };
  return accessToken;
};

// Custom hook for embedding dashboards
const useDashboardEmbed = (accessToken, dashboards) => {
  const getToken = async () => {
    if (!accessToken || dashboards.length === 0) return null;

    const resources = await dashboards.map((dashboard) => ({
      type: "dashboard",
      id: dashboard.embed_id, 
    }));

    try {
      const response = await handleEmbedDashboard({ resources });
      return response;
    } catch (error) {
      console.error("Error fetching guest token:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("superset_access_token");
        window.location.reload();
      }
      return null;
    }
  };

  const embed = async (dashboardId) => {
    const tokenResponse = await getToken();
    const guestToken = tokenResponse?.data?.guest_token || "";
  
    if (!guestToken) {
      console.error("Failed to fetch a valid guest token.");
      return;
    }
  
    const mountPoint = document.getElementById("dashboard");
    if (mountPoint) {
      try {
        await embedDashboard({
          id: dashboardId,
          supersetDomain: SUPERSET_BASE_URL,
          mountPoint,
          fetchGuestToken: async () => guestToken,
          dashboardUiConfig: {
            hideTitle: true,
            hideChartControls: true,
            hideTab: true,
          },
        });
  
        // Apply styles to the iframe
        setTimeout(() => {
          const iframe = mountPoint.querySelector("iframe");
          if (iframe) {
            iframe.style.width = "100%";
            iframe.style.height = "100vh";
            iframe.style.marginTop = "20px";
            iframe.style.border = "none";
          }
        }, 0);
      } catch (error) {
        console.error("Error embedding dashboard:", error);
      }
    }
  };
  return { embed };
};

const Dashboard = () => {
  const accessToken = useAccessToken();
  const navigate = useNavigate();
  const [permittedDashboards, setPermittedDashboards] = useState([]);
  const { embed } = useDashboardEmbed(accessToken, permittedDashboards);
  const [value, setValue] = useState(0);
  const tabsRef = useRef(null);
  const [openAddDashboard, setOpenAddDashboard] = useState(false);
  const scrollAmount = 150;
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        await getCurrentUserDetails();
      } catch (err) {
        if (err.message === "No active account") {
          toast.error("Session Expired, Please Log back", {
            theme: "colored",
          });
          return navigate("/");
        }
      }
    };
    getCurrentUser();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchDashboards();
  }, []);

  // Automatically embed the first dashboard
  useEffect(() => {
    if (accessToken && permittedDashboards.length > 0) {
      embed(permittedDashboards[0].embed_id); 
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, permittedDashboards]);

  const fetchDashboards = async () => {
    try {
      const response = await getDashboards();
      setPermittedDashboards(response.data?.filter((dashboard) => user.dashboard_perms?.includes(dashboard.name)) ?? []) 
    } catch (error) {
      console.error("Error fetching dashboards:", error);
    }
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const scrollTabs = (direction) => {
    if (tabsRef.current) {
      const scrollContainer = tabsRef.current.querySelector(".MuiTabs-scroller");
      if (scrollContainer) {
        scrollContainer.scrollBy({ left: direction === "left" ? -scrollAmount : scrollAmount, behavior: "smooth" });
      }
    }
  };

  const handleSaveDashboard = async (data) => {
    try {
      const response = await saveDashboards(data);
      if (response.status === 201) {
        fetchDashboards(); 
      }
      toast.success("Dashboard added successfully. Ensure you have access before viewing.", { theme: "colored" });
    } catch (error) {
      console.error("Error saving dashboard:", error);
    }
    setOpenAddDashboard(false);
  };

  return (
    <div>
      <Header />
      <Banner small />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, paddingRight: "20px", marginTop: !user?.add_dashboard && !user?.manage_dashboard ? "0px" :"-50px" }}>
        {user?.add_dashboard && (
          <StyledButton variant="contained" onClick={() => setOpenAddDashboard(true)}>
            Add New Dashboard
          </StyledButton>
        )}
        {user?.manage_dashboard && (
        <Link to={SUPERSET_BASE_URL} target="_blank"><StyledButton variant="contained" >Manage Dashboard</StyledButton></Link>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, marginTop: "40px" }}>
        <Button
          onClick={() => scrollTabs("left")}
          sx={{
            backgroundColor: "#006F352F",
            color: "#006F35",
            minWidth: "30px",
            height: "30px",
            borderRadius: "50%",
            "&:hover": { backgroundColor: "#006F35", color: "#FFFF" },
          }}
        >
          {"<"}
        </Button>

        <Box ref={tabsRef} sx={{ maxWidth: "70%", overflow: "hidden", display: "flex", borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons={false}>
            {permittedDashboards.length > 0 ? (
              permittedDashboards.map((dashboard, index) => (
                <Tab key={dashboard._id} label={dashboard.name} onClick={() => embed(dashboard.embed_id)} />
              ))
            ) : (
              <Tab label="No Dashboards" disabled />
            )}
          </Tabs>
        </Box>

        <Button
          onClick={() => scrollTabs("right")}
          sx={{
            backgroundColor: "#006F352F",
            color: "006F35",
            minWidth: "30px",
            height: "30px",
            borderRadius: "50%",
            "&:hover": { backgroundColor: "#006F35", color: "#FFFF" },
          }}
        >
          {">"}
        </Button>
      </Box>

      <div id="dashboard" style={{ width: "100%", marginTop: "8px" ,overflow: "auto"}} />

      <AddDashboardDialog open={openAddDashboard} onClose={() => setOpenAddDashboard(false)} onSave={handleSaveDashboard} />
    </div>
  );
};

export default Dashboard;
